<template>
  <div class="wrapper" :class="{ 'math-anim-isolated-wrapper': !plain }" ref="mdRef"></div>
</template>


<script>
import { mdRenderData } from "@/utils/markdown/MarkdownConverter";
import morphdom from "morphdom";

export default {
  name: "MarkdownRender",
  props: {
    markdown: String,
    plain: { type: Boolean, default: false },  // 新增
  },
  data() {
    return {
      markdownCache: {},
    };
  },
  mounted() {
    this.updateDOM();
    this.bindImageRetry();
  },
  activated() {
    this.updateDOM();
    this.bindImageRetry();
  },
  beforeDestroy() {
    this.$refs.mdRef?.removeEventListener("error", this.handleImageError, true);
  },
  methods: {
    renderMarkdown() {
      if (!this.markdown || this.markdown === "") return "";
      if (!this.markdownCache[this.markdown]) {
        this.markdownCache[this.markdown] = mdRenderData(this.markdown);
      }
      return this.markdownCache[this.markdown];
    },
    updateDOM() {
      const container = this.$refs.mdRef;
      if (!container) return;

      const newHtml = this.renderMarkdown();
      if (!newHtml) {
        container.innerHTML = "";
        return;
      }

      const temp = document.createElement("div");
      temp.innerHTML = newHtml;

      morphdom(container, temp, {
        childrenOnly: true,
        onBeforeElUpdated: (fromEl, toEl) => {
          if (fromEl.tagName === "DETAILS" && toEl.tagName === "DETAILS") {
            toEl.open = fromEl.open;
          }
        },
      });
    },
    bindImageRetry() {
      const container = this.$refs.mdRef;
      if (!container) return;
      container.removeEventListener("error", this.handleImageError, true);
      container.addEventListener("error", this.handleImageError, true);
    },
    handleImageError(e) {
      const img = e.target;
      if (img.tagName !== "IMG") return;

      const retryKey = "_retryCount";
      if (!img[retryKey]) img[retryKey] = 0;
      if (img[retryKey] >= 10) {
        // img.src = 'fallback.png'; // 最终失败的占位图
        return;
      }

      img[retryKey]++;
      const delay = Math.min(1000 * Math.pow(2, img[retryKey]), 10000);
      setTimeout(() => {
        const url = img.src.replace(/(\?|&)retry=\d+/, "");
        img.src = url + (url.includes("?") ? "&" : "?") + "retry=" + Date.now();
      }, delay);
    },
  },
  watch: {
    markdown(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (oldVal) {
          this.$delete(this.markdownCache, oldVal);
        }
        this.markdownCache[newVal] = mdRenderData(newVal);
        this.updateDOM();
      }
    },
  },
};
</script>

<!-- 注意：不要加 scoped，v-html / morphdom 动态生成的内容必须由全局样式命中 -->
<style>
.math-anim-isolated-wrapper {
  line-height: 1.8;
  font-size: 16px;
  color: #333;
  max-width: 1000px;
  margin: 0 auto;
  padding: 10px;
  box-sizing: border-box;
}
.math-anim-isolated-wrapper * {
  box-sizing: inherit;
}
.math-anim-isolated-wrapper blockquote {
  border-left: 4px solid #FF9800;
  background-color: #E8F5E9;
  padding: 1em 1.5em;
  margin: 1.5em 0;
  border-radius: 0 4px 4px 0;
}

/* ----- 表格横向滚动核心（纯 CSS 实现，无需 JS）----- */
.math-anim-isolated-wrapper table {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  white-space: nowrap;
  border-collapse: collapse;
  margin: 20px 0;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  table-layout: auto;
}
.math-anim-isolated-wrapper table td,
.math-anim-isolated-wrapper table th {
  border: 1px solid #e8e8e8;
  padding: 12px 16px;
  background: transparent;
  text-align: left;
  font-size: 14px;
  white-space: nowrap;
}
.math-anim-isolated-wrapper table th {
  background: linear-gradient(180deg, #f8f9fa 0%, #f5f5f5 100%);
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  border-bottom: 2px solid #e0e0e0;
}
.math-anim-isolated-wrapper table tr:nth-child(even) td {
  background-color: #fafafa;
}
.math-anim-isolated-wrapper table tr:hover td {
  background-color: #f0f7ff;
  border-color: #d1e7ff;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.math-anim-isolated-wrapper table td.numeric {
  text-align: right;
}

/* 折叠详情块 */
.math-anim-isolated-wrapper details {
  margin: 10px 0;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 8px 12px;
  display: block;
}
.math-anim-isolated-wrapper details[open] {
  background-color: #f8fafc;
  border-color: #c7d2fe;
}
.math-anim-isolated-wrapper details > *:not(summary) {
  margin-top: 8px;
  color: #4b5563;
}
.math-anim-isolated-wrapper summary {
  cursor: pointer;
  user-select: none;
  display: list-item;
  color: #6366f1;
  font-weight: 500;
}
.math-anim-isolated-wrapper summary:hover {
  color: #4f46e5;
}

/* 按钮 */
.math-anim-btn {
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  margin: 10px 0;
  display: inline-block;
  line-height: 1;
}
.math-anim-btn:hover {
  background: #4f46e5;
}
.math-anim-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

/* 动画 iframe 容器 */
.math-anim-container {
  width: 100%;
  min-height: 600px;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
  position: relative;
}
.math-anim-loading {
  text-align: center;
  padding: 50px 0;
  color: #666;
  font-size: 14px;
  margin: 0;
}
.math-anim-close {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 8px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  z-index: 10;
  line-height: 1;
}
.math-anim-close:hover {
  background: #ef4444;
  color: white;
  border-color: #dc2626;
}
.math-anim-iframe {
  width: 100%;
  height: 600px;
  border: none;
  display: block;
  margin: 0;
}

/* 手风琴折叠 */
.math-anim-collapse-wrapper {
  margin: 10px 0;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}
.math-anim-collapse-title {
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
  color: #6366f1;
  font-weight: 500;
  border-bottom: 1px solid #e2e8f0;
}
.math-anim-collapse-title:hover {
  color: #4f46e5;
  background-color: #f8fafc;
}
.math-anim-collapse-title.expanded {
  background-color: #f0f1ff;
  border-bottom: 1px solid #ddd;
}
.math-anim-collapse-content {
  padding: 8px 12px;
  display: none;
}
.math-anim-collapse-content.show {
  display: block;
}
</style>
